import { normalizeSubscriptionStatus } from './subscription-ui.js';

/**
 * Pure rules for the Mature Content (18+) experience: when to warn that the
 * server will refuse a filter-off render, when a delivered result that the
 * server labelled sensitive stays covered for the viewer, who may reveal it,
 * which key every view of a result shares, and which settings changes re-cover
 * everything.
 */

// Vibe pays every render in Spark, so SOGNI-paid access (which the socket also
// honours) is not a way to unlock the filter here.
export const MATURE_FILTER_PLAN_NOTE = 'Rendering with the filter off needs an Unlimited plan or Premium Spark.';

/**
 * The note to show under the Mature Content switch, or null.
 *
 * `unlocked` is the socket's own answer (`sensitiveContentFilterUnlocked`)
 * when the client has it. A Premium Spark balance unlocks even when that flag
 * says no: the flag is pushed on plan changes, while a Spark purchase only
 * refreshes the balance, which the server's admission check reads live. Without
 * the flag, the note shows only for an account that does not look entitled: no
 * Unlimited plan, no active or trialing subscription, and no Premium Spark.
 */
export function matureFilterPlanNote({ unlocked = null, isUnlimited = false, subscription = null, premiumSpark = 0 } = {}) {
  if (unlocked === true || Number(premiumSpark) > 0) return null;
  if (unlocked === false) return MATURE_FILTER_PLAN_NOTE;
  const status = normalizeSubscriptionStatus(subscription?.status);
  const entitledLooking = isUnlimited === true || subscription?.active === true
    || status === 'active' || status === 'trialing' || Number(premiumSpark) > 0;
  return entitledLooking ? null : MATURE_FILTER_PLAN_NOTE;
}

/**
 * A labelled result is covered unless this viewer revealed it. Signed out it is
 * always covered: favorites are device-local, and a passive sign-out keeps the
 * last user's switches in memory. Signed in, it shows uncovered by default only
 * with the filter off and the blur preference off.
 */
export function shouldCoverSensitive({ sensitive, isLoggedIn, matureContentEnabled, blurMatureContent, revealed }) {
  if (sensitive !== true || revealed) return false;
  return !isLoggedIn || !matureContentEnabled || blurMatureContent !== false;
}

/** Only a signed-in viewer who confirmed 18+ may uncover a labelled result. */
export function canRevealSensitive({ isLoggedIn = false, matureContentConfirmed = false } = {}) {
  return isLoggedIn === true && matureContentConfirmed === true;
}

/**
 * One reveal key per result, shared by every view of it: the project id (each
 * image request renders one image), then the job id, then a favorite id, then
 * the URL. Never the positional Studio imageId, which repeats across iterations.
 */
export function sensitiveRevealKey(item) {
  return String(item?.projectId || item?.jobId || item?.id || item?.url || '');
}

/**
 * A streaming frame (preview or rendering) of a request that turned the filter
 * off has no server verdict yet; treat it as labelled until the delivered
 * result's label arrives. A filter-on request withholds anything sensitive, so
 * its frames need no cover.
 */
export function streamingFrameMayBeSensitive(image) {
  return ['preview', 'rendering'].includes(image?.status) && image?.projectConfig?.disableNSFWFilter === true;
}

/** True when a settings change should re-cover every revealed result: the filter or the blur coming back on. */
export function coverResetNeeded(previous, next) {
  const filterBackOn = previous.matureContentEnabled === true && next.matureContentEnabled !== true;
  const blurBackOn = previous.blurMatureContent === false && next.blurMatureContent !== false;
  return filterBackOn || blurBackOn;
}
